import { useCallback } from 'react';
import {
  Sidebar,
  Button,
  Divider,
  Badge,
  DownloadIcon,
  SendIcon,
  TrashIcon,
} from '@forge/common';
import { useApiMutation } from '@/hooks/useApiMutation';
import { filesApi } from '@/api/files';
import type { FileRecord } from '@/api/files';

interface FileDetailSidebarProps {
  readonly file: FileRecord | undefined;
  readonly visible: boolean;
  readonly onClose: () => void;
  readonly onForward: (file: FileRecord) => void;
  readonly onDelete: (fileId: string) => void;
  readonly isOperator: boolean;
}

const formatFileSize = (bytes: number): string => {
  if (bytes >= 1048576) return `${(bytes / 1048576).toFixed(1)} MB`;
  if (bytes >= 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${bytes} B`;
};

const formatDate = (iso: string): string =>
  new Date(iso).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });

const statusColor = (status: string): 'green' | 'blue' | 'orange' | 'gray' => {
  switch (status) {
    case 'sent': return 'green';
    case 'new': return 'blue';
    case 'pending': return 'orange';
    case 'read': return 'gray';
    default: return 'gray';
  }
};

const FileDetailSidebar = ({
  file,
  visible,
  onClose,
  onForward,
  onDelete,
  isOperator,
}: FileDetailSidebarProps) => {
  const downloadMutation = useApiMutation<string, Blob>({
    mutationFn: (fileId) => filesApi.downloadFile(fileId),
    onSuccess: (blob) => {
      if (!file) return;
      const url = URL.createObjectURL(blob);
      const anchor = document.createElement('a');
      anchor.href = url;
      anchor.download = file.name;
      document.body.appendChild(anchor);
      anchor.click();
      document.body.removeChild(anchor);
      URL.revokeObjectURL(url);
    },
    errorMessage: 'Failed to download file',
  });

  const deleteMutation = useApiMutation<string, void>({
    mutationFn: (fileId) => filesApi.deleteFile(fileId),
    successMessage: 'File deleted',
    onSuccess: () => {
      if (file) onDelete(file.id);
      onClose();
    },
  });

  const handleDownload = useCallback(() => {
    if (file) downloadMutation.mutate(file.id);
  }, [file, downloadMutation]);

  const handleDelete = useCallback(() => {
    if (file) deleteMutation.mutate(file.id);
  }, [file, deleteMutation]);

  return (
    <Sidebar
      visible={visible}
      position="right"
      backdrop
      overlay
      animated={false}
      showClose
      onClose={onClose}
      style={{ width: 'min(30rem, 92vw)' }}
      header={<span className="font-bold text-sm">File Details</span>}
    >
      {file && (
        <div className="flex flex-col gap-4 p-4">
          <div>
            <p className="text-xs text-color-tertiary">File Name</p>
            <p className="text-sm font-medium break-all">{file.name}</p>
          </div>

          <div className="flex gap-6">
            <div>
              <p className="text-xs text-color-tertiary">Size</p>
              <p className="text-sm">{formatFileSize(file.size)}</p>
            </div>
            <div>
              <p className="text-xs text-color-tertiary">Type</p>
              <p className="text-sm">{file.type}</p>
            </div>
          </div>

          <div>
            <p className="text-xs text-color-tertiary">Status</p>
            <div className="mt-1">
              <Badge color={statusColor(file.status)}>
                {file.status.charAt(0).toUpperCase() + file.status.slice(1)}
              </Badge>
            </div>
          </div>

          <div>
            <p className="text-xs text-color-tertiary">Uploaded By</p>
            <p className="text-sm">{file.sender.name}</p>
          </div>

          <div>
            <p className="text-xs text-color-tertiary">Sent To</p>
            <p className="text-sm">{file.receiver.name}</p>
          </div>

          <div>
            <p className="text-xs text-color-tertiary">Date</p>
            <p className="text-sm">{formatDate(file.date)}</p>
          </div>

          <div>
            <p className="text-xs text-color-tertiary">Expires</p>
            <p className="text-sm">{formatDate(file.expires)}</p>
          </div>

          <div>
            <p className="text-xs text-color-tertiary">Checksum (SHA-256)</p>
            <p className="text-xs font-mono break-all text-color-secondary">{file.checksum}</p>
          </div>

          {file.tags.length > 0 && (
            <div>
              <p className="text-xs text-color-tertiary">Tags</p>
              <div className="flex flex-wrap gap-1 mt-1">
                {file.tags.map((tag) => (
                  <span
                    key={tag}
                    className="px-2 py-0.5 text-xs rounded bg-gray-100 dark:bg-gray-800"
                  >
                    {tag}
                  </span>
                ))}
              </div>
            </div>
          )}

          <Divider className="my-2" />

          <div className="flex flex-col gap-2">
            <Button variant="primary" onClick={handleDownload} disabled={downloadMutation.isSubmitting}>
              <DownloadIcon />
              <span className="ml-1">{downloadMutation.isSubmitting ? 'Downloading…' : 'Download'}</span>
            </Button>
            {isOperator && (
              <Button variant="secondary" onClick={() => onForward(file)}>
                <SendIcon />
                <span className="ml-1">Forward</span>
              </Button>
            )}
            <Button variant="tertiary" onClick={handleDelete} disabled={deleteMutation.isSubmitting}>
              <TrashIcon />
              <span className="ml-1">{deleteMutation.isSubmitting ? 'Deleting…' : 'Delete'}</span>
            </Button>
          </div>
        </div>
      )}
    </Sidebar>
  );
};

export default FileDetailSidebar;
